import React, { useState, useEffect, useRef, useMemo } from 'react';
import { X, Upload, Loader2, Wand2, ListTodo } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { useContentSelection } from '@/hooks/shared/useContentSelection';
import { TaskItemCard } from './TaskItemCard';
import { useSuggestedTasksQuery } from '@/hooks/goal-planner/queries/use-suggested-tasks-query';
import { useBulkTaskGeneration } from '@/hooks/goal-planner/useBulkTaskGeneration';
import { toast } from 'sonner';

interface TaskRecommendationImporterProps {
  goalId: string;
  goalTitle: string;
  goalDescription?: string;
  openaiThreadId?: string;
  onBulkAddToProject: (selectedTaskIds: string[]) => Promise<void>;
  onBulkClearSelection: (selectedTaskIds: string[], selectedTasks: any[]) => void;
  className?: string;
  isProcessingFeedback?: boolean;
  hideClearSelection?: boolean; // Hide clear selection button for simplified UX
  contextInput?: string; // NEW: User context for AI generation
  onContextInputChange?: (value: string) => void; // NEW: Context change handler
  hideContextInput?: boolean; // NEW: Hide the context input bar (for initial generation)
  onGenerateClick?: () => void; // NEW: Custom handler for generate button click
  isExternallyGenerating?: boolean; // NEW: External generation state (from container)
}

export const TaskRecommendationImporter: React.FC<TaskRecommendationImporterProps> = ({
  goalId,
  goalTitle,
  goalDescription,
  openaiThreadId,
  onBulkAddToProject,
  onBulkClearSelection,
  className,
  isProcessingFeedback = false,
  hideClearSelection = false,
  contextInput = '',
  onContextInputChange,
  hideContextInput = false,
  onGenerateClick,
  isExternallyGenerating = false
}) => {
  const [isAddingToProject, setIsAddingToProject] = useState(false);

  // Task data
  const { data: allTasks = [], isLoading } = useSuggestedTasksQuery(goalId);
  
  // Bulk generation
  const bulkGenerationMutation = useBulkTaskGeneration(goalId);

  // Selection functionality
  const {
    selectedItems,
    toggleItemSelection,
    clearSelection,
    isSelected,
    selectedCount
  } = useContentSelection();

  // Get selected tasks for bulk operations
  const selectedTasks = useMemo(() => {
    return allTasks.filter(task => selectedItems.has(task.id));
  }, [allTasks, selectedItems]);

  // Handle context input change
  const handleContextInputChange = (value: string) => {
    if (onContextInputChange) {
      onContextInputChange(value);
    }
  };

  // Clear context input
  const handleClearContext = () => {
    handleContextInputChange('');
  };

  // Handle bulk add to project
  const handleBulkAddToProject = async () => {
    if (selectedCount === 0) return;
    
    setIsAddingToProject(true);
    try {
      await onBulkAddToProject(Array.from(selectedItems));
      clearSelection(); // Clear selections after successful import
      toast.success(`Added ${selectedCount} task${selectedCount > 1 ? 's' : ''} to Team Tasks!`);
    } catch (error) {
      console.error('Error adding tasks to project:', error);
      toast.error('Failed to add tasks to project');
    } finally {
      setIsAddingToProject(false);
    }
  };

  // Handle bulk clear selection
  const handleBulkClearSelection = () => {
    if (selectedCount === 0) return;
    
    const selectedTaskIds = Array.from(selectedItems);
    onBulkClearSelection(selectedTaskIds, selectedTasks);
    clearSelection();
  };

  // Handle generate tasks
  const handleGenerateTasks = async () => {
    // If custom handler provided (for initial generation), use it
    if (onGenerateClick) {
      onGenerateClick();
      return;
    }
    
    if (!openaiThreadId) {
      toast.error("Cannot generate tasks - goal missing OpenAI thread");
      return;
    }

    try {
      await bulkGenerationMutation.mutateAsync({
        goalId,
        goalTitle,
        goalDescription,
        openaiThreadId,
        userContext: contextInput || undefined
      });
    } catch (error) {
      // Error handling is done in the mutation's onError
      console.error('Generate tasks error:', error);
    }
  };

  const isGenerating = bulkGenerationMutation.isPending || isExternallyGenerating;
  const isBlocked = isGenerating || isProcessingFeedback;

  return (
    <div className={cn("flex flex-col h-full", className)}>
      {/* Context Input Bar - Moved to top - Only show if not hideContextInput */}
      {!hideContextInput && (
        <div className="px-4 py-3 border-b border-gray-200/50 dark:border-gray-700/50">
          <div className="relative">
            <Wand2 className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10 pointer-events-none" size={16} />
            <Input
              placeholder="What kind of tasks should we generate?"
              value={contextInput}
              onChange={(e) => handleContextInputChange(e.target.value)}
              className="pl-9 pr-9 relative z-0"
            />
            {contextInput && (
              <button
                onClick={handleClearContext}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 z-10"
                title="Clear context"
                aria-label="Clear context"
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Action Buttons Row - Shows Generate Tasks when empty, Add to Project when tasks exist */}
      <div className="px-4 py-3 border-b border-gray-200/50 dark:border-gray-700/50">
        <div className="flex items-center gap-2">
          {allTasks.length === 0 ? (
            // Generate Tasks Button (when no tasks exist)
            <Button
              onClick={handleGenerateTasks}
              disabled={isBlocked}
              className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
              size="sm"
              data-generate-tasks-button
            >
              {isGenerating ? (
                <>
                  <Loader2 size={14} className="mr-2 animate-spin" />
                  Generating Tasks...
                </>
              ) : isProcessingFeedback ? (
                <>
                  <Loader2 size={14} className="mr-2 animate-spin" />
                  Processing Feedback...
                </>
              ) : (
                <>
                  <Wand2 size={14} className="mr-2" />
                  Generate Tasks
                </>
              )}
            </Button>
          ) : (
            // Add to Project Button (when tasks exist)
            <>
              <Button
                onClick={handleBulkAddToProject}
                disabled={selectedCount === 0 || isAddingToProject}
                className={cn(
                  "flex-1 text-white",
                  selectedCount === 0 
                    ? "bg-amber-400 hover:bg-amber-400 cursor-not-allowed opacity-60" 
                    : "bg-amber-600 hover:bg-amber-700"
                )}
                size="sm"
              >
                {isAddingToProject ? (
                  <>
                    <Loader2 size={14} className="mr-2 animate-spin" />
                    Adding to Team Tasks...
                  </>
                ) : (
                  <>
                    <Upload size={14} className="mr-2" />
                    Add to Project {selectedCount > 0 ? `(${selectedCount})` : ''}
                  </>
                )}
              </Button>
              
              {!hideClearSelection && (
                <Button
                  onClick={handleBulkClearSelection}
                  disabled={selectedCount === 0 || isBlocked}
                  variant="outline"
                  size="sm"
                  className="hidden sm:inline-flex"
                >
                  {isGenerating ? 'Generating...' : isProcessingFeedback ? 'Processing...' : 'Clear Selection'}
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Tasks List */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 size={20} className="animate-spin text-gray-400" />
            <span className="ml-2 text-sm text-gray-500">Loading task recommendations...</span>
          </div>
        ) : allTasks.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <ListTodo className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-4" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No tasks generated yet. Click "Generate Tasks" above to get AI-powered task recommendations.
            </p>
          </div>
        ) : (
          <div className="p-2">
            <AnimatePresence>
              {allTasks.map((task, index) => (
                <TaskItemCard
                  key={task.id}
                  task={task}
                  isSelected={isSelected(task.id)}
                  onToggleSelection={toggleItemSelection}
                  index={index}
                />
              ))}
            </AnimatePresence>
            
            {/* Generate More Tasks Button */}
            {allTasks.length > 0 && (
              <div className="flex items-center justify-center py-4">
                <Button
                  onClick={handleGenerateTasks}
                  disabled={isBlocked}
                  variant="outline"
                  className="bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-950/30 dark:to-yellow-950/30 border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/40"
                  data-generate-tasks-button
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating 5 More Tasks...
                    </>
                  ) : isProcessingFeedback ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing Feedback...
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-4 h-4 mr-2" />
                      Generate 5 More Tasks
                    </>
                  )}
                </Button>
              </div>
            )}
            
            {/* End of Results */}
            {allTasks.length > 0 && (
              <div className="flex items-center justify-center py-4">
                <span className="text-xs text-gray-400">
                  {allTasks.length} task recommendation{allTasks.length > 1 ? 's' : ''}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}; 